import React, { useState } from 'react';
import { Language, KolCategory, ProjectProfile, PaymentType } from '../types';
import { TRANSLATIONS, getCategoryLabel } from '../translations';
import { Megaphone, CheckCircle, Wallet, Coins, Clock, Info } from 'lucide-react';

interface CreateCampaignViewProps {
  language: Language;
  onPostCampaign: (campaign: ProjectProfile) => void;
}

export const CreateCampaignView: React.FC<CreateCampaignViewProps> = ({ language, onPostCampaign }) => {
  const t = TRANSLATIONS[language];
  const [success, setSuccess] = useState(false);
  const [isStaking, setIsStaking] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    industry: KolCategory.DEFI,
    minBudget: '',
    maxBudget: '',
    website: '',
    description: '',
    tags: '',
    paymentType: PaymentType.USDT,
    tokenSymbol: '',
    tokenAmount: '',
    vestingTerms: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsStaking(true);

    // Simulate smart contract interaction (Staking)
    setTimeout(() => {
        const newCampaign: ProjectProfile = {
            id: `camp_${Date.now()}`,
            name: formData.name, 
            logo: `https://picsum.photos/100/100?random=${Math.floor(Math.random() * 100)}`,
            industry: formData.industry,
            description: formData.description,
            website: formData.website,
            budgetRange: `$${formData.minBudget} - $${formData.maxBudget}`,
            tags: formData.tags.split(',').map(tag => tag.trim()).filter(t => t !== ''),
            activeCampaigns: 1,
            verified: true,
            escrowed: true,
            applications: [],
            paymentType: formData.paymentType,
            tokenDetails: formData.paymentType === PaymentType.HYBRID ? {
                symbol: formData.tokenSymbol,
                amount: Number(formData.tokenAmount),
                vestingTerms: formData.vestingTerms
            } : undefined,
            points: 0 // Initialize points for new campaign
        };

        onPostCampaign(newCampaign);
        setIsStaking(false);
        setSuccess(true);
        
        // Reset form
        setTimeout(() => {
            setSuccess(false);
            setFormData({
                name: '',
                industry: KolCategory.DEFI,
                minBudget: '',
                maxBudget: '',
                website: '',
                description: '',
                tags: '',
                paymentType: PaymentType.USDT,
                tokenSymbol: '',
                tokenAmount: '',
                vestingTerms: ''
            });
        }, 3000);
    }, 2000);
  };

  const calculateTotalStaked = () => {
      const max = Number(formData.maxBudget) || 0;
      const fee = max * 0.20;
      return (max + fee).toFixed(2);
  };

  return (
    <div className="max-w-3xl mx-auto animate-in fade-in duration-500">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
           <Megaphone className="text-indigo-400" />
           {t.createCampaignTitle}
        </h1>
        <p className="text-slate-400 mt-1">{t.createCampaignSubtitle}</p>
      </div>

      {success ? (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-8 text-center animate-in zoom-in-95 duration-300">
           <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-emerald-400" />
           </div>
           <h2 className="text-xl font-bold text-white mb-2">{t.campaignPostedSuccess}</h2>
           <p className="text-slate-300">{t.campaignPostedDesc}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-slate-800 border border-slate-700 rounded-xl p-8 space-y-6">
           {/* Staking Notice */}
           <div className="bg-indigo-900/20 border border-indigo-500/30 rounded-lg p-4 flex gap-3">
              <Wallet className="text-indigo-400 flex-shrink-0" />
              <div className="text-sm text-indigo-200">
                <p>{t.stakeInfo}</p>
              </div>
            </div>

           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                 <label className="block text-sm font-medium text-slate-300 mb-2">{t.campaignName}</label>
                 <input 
                    type="text" 
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g. Summer Launch Campaign"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                 />
              </div>
              <div>
                 <label className="block text-sm font-medium text-slate-300 mb-2">{t.campaignIndustry}</label>
                 <select 
                    value={formData.industry}
                    onChange={(e) => setFormData({...formData, industry: e.target.value as KolCategory})}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                 >
                    {Object.values(KolCategory).map(cat => (
                        <option key={cat} value={cat}>{getCategoryLabel(cat, language)}</option>
                    ))}
                 </select>
              </div>
           </div>

           {/* Payment Type Selection */}
           <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700 space-y-4">
               <label className="block text-sm font-medium text-slate-300">{t.paymentType}</label>
               <div className="grid grid-cols-2 gap-4">
                   <button
                       type="button"
                       onClick={() => setFormData({...formData, paymentType: PaymentType.USDT})}
                       className={`flex items-center justify-center gap-2 py-3 rounded-lg border transition-all ${
                           formData.paymentType === PaymentType.USDT 
                           ? 'bg-emerald-600 border-emerald-500 text-white shadow-lg' 
                           : 'bg-slate-800 border-slate-600 text-slate-400 hover:bg-slate-700'
                       }`}
                   >
                       <Wallet size={18} />
                       {t.usdtOnly}
                   </button>
                   <button
                       type="button"
                       onClick={() => setFormData({...formData, paymentType: PaymentType.HYBRID})}
                       className={`flex items-center justify-center gap-2 py-3 rounded-lg border transition-all ${
                           formData.paymentType === PaymentType.HYBRID 
                           ? 'bg-purple-600 border-purple-500 text-white shadow-lg' 
                           : 'bg-slate-800 border-slate-600 text-slate-400 hover:bg-slate-700'
                       }`}
                   >
                       <Coins size={18} />
                       {t.hybridPayment}
                   </button>
               </div>

               {/* Budget Inputs */}
               <div>
                 <label className="block text-sm font-medium text-slate-300 mb-2">{t.campaignBudget} (USDT Base)</label>
                 <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
                    <div className="flex-1 w-full">
                        <label className="block text-xs text-slate-400 mb-2 ml-1">{t.minBudget}</label>
                        <div className="relative">
                            <input 
                                type="number" 
                                min="0"
                                required
                                value={formData.minBudget}
                                onChange={(e) => setFormData({...formData, minBudget: e.target.value})}
                                placeholder="1000"
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-4 pr-16 py-3 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium pointer-events-none">USDT</span>
                        </div>
                    </div>
                    
                    <div className="hidden sm:flex h-12 items-center justify-center pb-2">
                        <span className="text-slate-500 font-medium">-</span>
                    </div>
                    
                    <div className="flex-1 w-full">
                        <label className="block text-xs text-slate-400 mb-2 ml-1">{t.maxBudget}</label>
                        <div className="relative">
                            <input 
                                type="number" 
                                min="0"
                                required
                                value={formData.maxBudget}
                                onChange={(e) => setFormData({...formData, maxBudget: e.target.value})}
                                placeholder="5000"
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-4 pr-16 py-3 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium pointer-events-none">USDT</span>
                        </div>
                    </div>
                 </div>
               </div>

               {/* Fee Breakdown */}
               {formData.maxBudget && (
                   <div className="bg-slate-800 p-4 rounded-lg border border-slate-700 flex flex-col gap-2 animate-in fade-in">
                        <div className="flex justify-between text-xs text-slate-400">
                            <span>Max Budget (To KOL)</span>
                            <span>{Number(formData.maxBudget).toFixed(2)} USDT</span>
                        </div>
                        <div className="flex justify-between text-xs text-amber-500">
                            <span>Platform Fee (20%)</span>
                            <span>+{(Number(formData.maxBudget) * 0.20).toFixed(2)} USDT</span>
                        </div>
                        <div className="border-t border-slate-700 my-1"></div>
                        <div className="flex justify-between text-sm font-bold text-white">
                            <span>Total Staked</span>
                            <span>{calculateTotalStaked()} USDT</span>
                        </div>
                   </div>
               )}

               {/* Hybrid Payment Details */}
               {formData.paymentType === PaymentType.HYBRID && (
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 animate-in fade-in slide-in-from-top-2">
                       <div>
                           <label className="block text-sm font-medium text-slate-300 mb-2">{t.tokenSymbol}</label>
                           <input 
                               type="text" 
                               required
                               placeholder="e.g. SNOWA"
                               value={formData.tokenSymbol}
                               onChange={(e) => setFormData({...formData, tokenSymbol: e.target.value.toUpperCase()})}
                               className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                           />
                       </div>
                       <div>
                           <label className="block text-sm font-medium text-slate-300 mb-2">{t.tokenAmount} (Total)</label>
                           <input 
                               type="number" 
                               required
                               placeholder="10000"
                               value={formData.tokenAmount}
                               onChange={(e) => setFormData({...formData, tokenAmount: e.target.value})}
                               className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                           />
                       </div>
                       <div className="md:col-span-2">
                           <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                               <Clock size={14} className="text-purple-400" />
                               {t.vestingTerms}
                           </label>
                           <input 
                               type="text" 
                               required
                               placeholder={t.vestingPlaceholder}
                               value={formData.vestingTerms}
                               onChange={(e) => setFormData({...formData, vestingTerms: e.target.value})}
                               className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                           />
                       </div>
                   </div>
               )}
           </div>

           <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">{t.campaignWebsite}</label>
              <input 
                type="url" 
                required
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                placeholder="https://example.com"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
           </div>

           <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">{t.campaignDescription}</label>
              <textarea 
                 rows={5}
                 required
                 value={formData.description}
                 onChange={(e) => setFormData({...formData, description: e.target.value})}
                 placeholder={t.briefPlaceholder}
                 className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
           </div>

           <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">{t.campaignTags}</label>
              <input 
                 type="text" 
                 value={formData.tags}
                 onChange={(e) => setFormData({...formData, tags: e.target.value})}
                 placeholder="DeFi, Launch, Thread..."
                 className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
           </div>

           <div className="pt-4">
              <button 
                 type="submit"
                 disabled={isStaking}
                 className={`w-full bg-indigo-600 hover:bg-indigo-700 text-white py-4 rounded-xl font-bold shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2 text-lg ${isStaking ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                 {isStaking ? (
                     <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Staking {calculateTotalStaked()} USDT...
                     </>
                 ) : (
                     <>
                        <Wallet size={20} />
                        {t.postCampaignButton}
                     </>
                 )}
              </button>
           </div>
        </form>
      )}
    </div>
  );
};